'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import Image from 'next/image';
import { ReactNode } from 'react';

import { CallButton, LocationSubmit } from '@/app/components';
import { Eyebrow } from '@/app/home-solar/components';
import { bgDotsDark, sampleQuotes } from '@/app/lp/assets';
import { ButtonLink, ColumnSection, H2, H3, NumberedStep, Section, TrustIndicators } from '@/app/lp/components';
import { TRACKING } from '@/helpers/tracking';

import aerialNeighborhoodSolarPanels from './aerial-neighborhood-solar-panels.webp';

const GetEstimatesButton = () => (
  <ButtonLink
    event={{ name: TRACKING.LANDING_CLICK_GET_QUOTE }}
    icon={DS.IconArrowRight}
    iconPosition="right"
    path="/quote-builder"
    size={{ _: 'sm', md: 'md' }}
    variant="cta"
  >
    Get your estimates
  </ButtonLink>
);

const CtaButtons = () => (
  <DS.Flex
    alignItems="center"
    flexDirection={{ _: 'column', md: 'row' }}
    gap={{ _: 3, md: 4 }}
    justifyContent="center"
    w="fit-content"
  >
    <GetEstimatesButton />
    <CallButton variant="primary" />
  </DS.Flex>
);

const FeatureIcon = ({ icon: Icon }: { icon: typeof DS.IconLockClosed }) => <Icon size={8} />;

const DifferentiatorRow = ({ children, icon: Icon }: { children: ReactNode; icon: typeof DS.IconSun }) => (
  <DS.Flex alignItems="flex-start" flexDirection="row" gap={3}>
    <Icon size={5} />
    <DS.Text color="uiHelperCopy" flex={1} fontWeight={500} minW={0} text="body14">
      {children}
    </DS.Text>
  </DS.Flex>
);

const DifferentiatorCard = () => (
  <DS.LightModeProvider>
    <DS.Flex bg="uiCard.heroCardBg" borderRadius={12} flex={{ lg: '2 1 0' }} gap={5} maxW={{ lg: '460px' }} p={6}>
      <Eyebrow color="purple-400">How Aurora is different</Eyebrow>
      <DS.Flex>
        <DifferentiatorRow icon={DS.IconSun}>
          <x.span color="uiHeadingCopy">We&rsquo;re not an installer</x.span>
          <span> — we help you decide who you want to work with.</span>
        </DifferentiatorRow>
        <DS.Divider my={2} />
        <DifferentiatorRow icon={DS.IconMoney}>
          <span>See what solar would cost for your home and </span>
          <x.span color="uiHeadingCopy">compare estimates from local companies</x.span>
          <span>.</span>
        </DifferentiatorRow>
        <DS.Divider my={2} />
        <DifferentiatorRow icon={DS.IconAlertShield}>
          <span>Your information goes </span>
          <x.span color="uiHeadingCopy">only to the installers you choose</x.span>
          <span>.</span>
        </DifferentiatorRow>
      </DS.Flex>
    </DS.Flex>
  </DS.LightModeProvider>
);

export const HowItWorksPage = () => {
  return (
    <article>
      {/* S1 — hero (Grace's Figma "How it Works Hero Option 1": location entry + differentiator card) */}
      <DS.DarkModeProvider>
        <DS.Flex mt={-60} position="relative" px={6}>
          <Image
            alt=""
            fill
            priority
            sizes="100vw"
            src={aerialNeighborhoodSolarPanels}
            style={{ filter: 'brightness(55%)', objectFit: 'cover' }}
          />
          <Image alt="" fill sizes="100vw" src={bgDotsDark} style={{ objectFit: 'cover' }} />
          <DS.Flex
            alignItems={{ _: 'flex-start', lg: 'center' }}
            flexDirection={{ _: 'column', lg: 'row' }}
            gap={{ _: 7, lg: 10 }}
            maxW={DS.BorealisTheme.screens.xl}
            mx="auto"
            pb={{ _: 8, lg: 9 }}
            position="relative"
            pt={{ _: 110, lg: 130 }}
            zIndex={1}
          >
            <DS.Flex alignItems="flex-start" flex={{ lg: '3 1 0' }} gap={4}>
              <Eyebrow color="yellow-200">Your whole-home energy marketplace</Eyebrow>
              <DS.Text as="h1" text={{ _: 'd1', lg: 'd3', md: 'd2' }}>
                Compare local installers before you talk to anyone.
              </DS.Text>
              <DS.Text text={{ _: 'body16', md: 'body20' }}>
                Aurora gives you estimates for your actual home — no email, phone number, or account required.
              </DS.Text>
              <DS.Text text="body16">
                Solar shopping usually means handing over your contact information before you know what anything costs.
                Then you&rsquo;re getting sales calls before you&rsquo;re ready.
              </DS.Text>
              <DS.Text text="body16">
                We built the Aurora Marketplace to flip that. We&rsquo;re not an installer. We help you see what solar
                would cost for your home, compare estimates from local companies, and decide who you want to work with.
                Your information goes only to the installers you choose.
              </DS.Text>
              <DS.Flex gap={2} maxW="443px" mt={2}>
                <LocationSubmit gap={2} variant="cta">
                  Get your estimates
                </LocationSubmit>
                <TrustIndicators />
              </DS.Flex>
            </DS.Flex>
            <DifferentiatorCard />
          </DS.Flex>
        </DS.Flex>
      </DS.DarkModeProvider>

      {/* S2 — How the marketplace works for you */}
      <Section align="center">
        <H2>Multiple installers. One place to compare and decide.</H2>
      </Section>
      <Section columns={3}>
        <DS.Flex flex={1} gap={4}>
          <FeatureIcon icon={DS.IconLockClosed} />
          <H3>See estimates without sharing your contact information.</H3>
          <p>
            You get estimates from local installers — system size, pricing, and payment options — before sharing any
            contact information. Once you&rsquo;re ready, you choose who to hear from.
          </p>
        </DS.Flex>
        <DS.Flex flex={1} gap={4}>
          <FeatureIcon icon={DS.IconChartComparison} />
          <H3>Compare installers on more than just price.</H3>
          <p>
            <span>
              Each estimate includes the full picture — monthly payment, 25-year savings, payback period, equipment, and
              the company behind the offer — so you can decide based on what matters to{' '}
            </span>
            <strong>you</strong>
            <span>, not just the lowest number.</span>
          </p>
        </DS.Flex>
        <DS.Flex flex={1} gap={4}>
          <FeatureIcon icon={DS.IconCheckCircle} />
          <H3>No cold calls, no door knocks</H3>
          <p>
            Pick the installers you want to hear from and how you want them to contact you. Only the companies you
            choose will reach out.
          </p>
        </DS.Flex>
      </Section>
      <Section align="center">
        <DS.Text as="blockquote" maxW="700px" mx="auto" text={{ _: 'h3', md: 'h2' }}>
          &ldquo;I was just trying to do the smart thing and get multiple conversations going and multiple perspectives
          on what my home would need. And that was kind of the best way to educate myself&hellip;.&rdquo;
        </DS.Text>
        <DS.Text color="uiHelperCopy" text="body16">
          — Justin, Oregon
        </DS.Text>
      </Section>

      {/* Callout — full-width break (doc: treatment TBD, dark chosen for contrast) */}
      <Section align="center" colorMode="dark">
        <H2>We&rsquo;re not an installer. We help you compare.</H2>
        <DS.Text maxW="700px" mx="auto" text="body16">
          We don&rsquo;t sell solar. We&rsquo;re the marketplace that makes it easy to see what solar would cost for
          your home and choose the company you want to work with on your own terms.
        </DS.Text>
        <CtaButtons />
        {/* TODO: confirm support hours before publishing */}
        <DS.Text color="uiHelperCopy" text="body14">
          Questions before you choose? Our team is available by phone, chat, or text.
        </DS.Text>
      </Section>

      {/* S3 — Where the numbers come from */}
      {/* TODO: replace image with product UI / estimate screenshot (owner: Grace Boto) */}
      <Section bg="uiBgAlternate" imageRight={{ alt: 'Sample installer estimates', height: 520, src: sampleQuotes }}>
        <H2>Your estimate is built on your home, not a guess.</H2>
        <p>
          <span>
            When you enter your address, we don&rsquo;t look up your ZIP code and apply a regional average. We
            model{' '}
          </span>
          <strong>your specific roof</strong>
          <span>
            {' '}
            — its size, orientation, and the shade it gets — and pair that with your utility rate and actual electricity
            usage.
          </span>
        </p>
        <p>
          The pricing you see comes from the installers serving your area, applied to that modeled system. We show you
          every major way to pay — cash, loan, lease, or power-purchase agreement (PPA) — for your system at the same
          time, so you&rsquo;re comparing apples to apples across installers and payment methods.
        </p>
        <p>
          You can expect some differences between your initial estimate and your final cost after the installer visits
          your house. This is normal as sometimes a visit can show details that satellite imagery can&rsquo;t.
        </p>
      </Section>

      {/* S4 — What happens after you get your estimates */}
      <Section align="center">
        <H2>You&rsquo;re in control of what comes next.</H2>
      </Section>
      <ColumnSection columns={4}>
        <DS.Flex flex={1}>
          <NumberedStep>1</NumberedStep>
          <H3>Review</H3>
          <p>Compare systems, pricing, payment types, and the installers behind each estimate — at your own pace.</p>
        </DS.Flex>
        <DS.Flex flex={1}>
          <NumberedStep>2</NumberedStep>
          <H3>Customize</H3>
          <p>
            Adjust panel count or add battery storage. Your estimates update in real time, so you can find the right
            setup before you talk to anyone.
          </p>
        </DS.Flex>
        <DS.Flex flex={1}>
          <NumberedStep>3</NumberedStep>
          <H3>Choose</H3>
          <p>Select the installers you want to hear from.</p>
        </DS.Flex>
        <DS.Flex flex={1}>
          <NumberedStep>4</NumberedStep>
          <H3>Meet and decide</H3>
          <p>Schedule site visits with your installers to confirm your home&rsquo;s details and finalize the design.</p>
        </DS.Flex>
      </ColumnSection>

      {/* CTA band — maroon (approved yellow-on-maroon combo; gives the two CTAs a clear hierarchy) */}
      <Section align="center" bg="maroon-800" colorMode="dark">
        <H2>See what solar would cost for your actual home.</H2>
        <CtaButtons />
      </Section>
    </article>
  );
};
